import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TestimonialCarousel from '../components/TestimonialCarousel';
import TrustBadges from '../components/TrustBadges';
import PortfolioSection from '../components/PortfolioSection';
import { useTranslation } from 'react-i18next';

const { FiCheckCircle, FiUsers, FiCamera, FiShield, FiTrendingUp, FiBarChart3 } = FiIcons;

const Home = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: FiCamera,
      title: 'Marketing & Visibilità',
      description: 'Ottimizzazione SEO e fotografia professionale per dominare il mercato.'
    },
    {
      icon: FiUsers,
      title: 'Guest Management H24',
      description: 'Assistenza multilingue e gestione prenotazioni senza pensieri.'
    },
    {
      icon: FiCheckCircle,
      title: 'Housekeeping d\'Élite',
      description: 'Standard di pulizia certificati e manutenzione programmata.'
    },
    {
      icon: FiBarChart3,
      title: 'Revenue Optimization',
      description: 'Algoritmi di pricing dinamico per massimizzare il ROI.'
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="bg-white py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-brand-blue/5 -skew-x-12 transform translate-x-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="bg-brand-silver/30 text-brand-blue px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-6 inline-block border border-brand-silver">
                Eccellenza Immobiliare
              </span>
              <h1 className="text-5xl md:text-7xl font-extrabold text-brand-blue mb-6 leading-tight">
                Castello <span className="text-brand-gold">SRL</span>
              </h1>
              <p className="text-xl text-brand-subtle mb-10 leading-relaxed max-w-xl">
                Trasformiamo il tuo immobile in un asset ad alto rendimento con una gestione <span className="text-brand-blue font-bold">Premium a Milano</span>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/simulatore" 
                  className="bg-brand-gold text-white px-10 py-5 rounded-xl text-lg font-bold hover:bg-brand-blue transition-all shadow-xl hover:shadow-brand-gold/20 text-center"
                >
                  CALCOLA RENDITA
                </Link>
                <Link 
                  to="/contatti" 
                  className="bg-white text-brand-blue border-2 border-brand-blue px-10 py-5 rounded-xl text-lg font-bold hover:bg-brand-blue hover:text-white transition-all text-center"
                >
                  CONSULENZA GRATUITA
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-brand-gold/10 rounded-[2rem] blur-2xl"></div>
              <img 
                src="https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvanBlZw==/d31c996d43abab438db6a2eaffc18e56.jpg" 
                alt="Castello SRL Premium Property Milano" 
                className="rounded-3xl shadow-2xl h-[550px] w-full object-cover relative z-10 border-8 border-white"
              />
              
              <div className="absolute -bottom-8 -left-8 bg-brand-blue p-8 rounded-2xl shadow-2xl border border-brand-gold/30 z-20">
                <div className="flex items-center space-x-4 mb-2">
                  <div className="bg-brand-gold p-2 rounded-lg">
                    <SafeIcon icon={FiTrendingUp} className="text-white h-6 w-6" />
                  </div>
                  <span className="font-bold text-white text-2xl">+45% ROI</span>
                </div>
                <p className="text-sm text-brand-silver">Incremento medio ricavi a Milano</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* Services Grid */}
      <section className="py-24 bg-brand-blue/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-brand-blue mb-6">Servizi di Gestione Istituzionale</h2>
            <div className="w-24 h-1 bg-brand-gold mx-auto mb-6"></div>
            <p className="text-xl text-brand-subtle max-w-2xl mx-auto">Standard elevati per proprietari esigenti nel mercato milanese.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {services.map((s, i) => (
              <motion.div 
                key={i}
                whileHover={{ y: -10 }}
                className="bg-white p-10 rounded-2xl shadow-xl border border-brand-silver/20 group hover:border-brand-gold/50 transition-all"
              >
                <div className="bg-brand-blue w-16 h-16 rounded-2xl flex items-center justify-center mb-8 shadow-lg group-hover:bg-brand-gold transition-colors">
                  <SafeIcon icon={s.icon} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-brand-blue mb-4">{s.title}</h3>
                <p className="text-brand-subtle leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PortfolioSection />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TestimonialCarousel />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-brand-blue relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-brand-gold rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-gold rounded-full blur-[120px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-10 leading-tight">
            Eleva il tuo Patrimonio a Milano con <br/>
            <span className="text-brand-gold">Castello SRL</span>
          </h2>
          <p className="text-2xl text-brand-silver mb-12 max-w-3xl mx-auto font-light">
            Affidati al leader nel Property Management istituzionale. Esperienza, dati e risultati garantiti sul territorio milanese.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-8">
            <Link 
              to="/simulatore" 
              className="bg-brand-gold text-white px-12 py-6 rounded-2xl text-xl font-bold hover:bg-white hover:text-brand-blue transition-all shadow-2xl"
            >
              SIMULA IL TUO PROFITTO
            </Link>
            <Link 
              to="/contatti" 
              className="bg-transparent text-white border-2 border-brand-gold px-12 py-6 rounded-2xl text-xl font-bold hover:bg-brand-gold transition-all"
            >
              PARLA CON IL TEAM
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;